import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Building2, LogIn, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { adminApi } from "../../lib/api";

/**
 * Sponsor Portal login. Reuses the existing LifeGuide password flow — no
 * separate credential store. After a successful sign-in we call
 * `/api/admin/whoami` to check `sponsor_admin_bindings`; if empty we render
 * an Access Denied state without ever exposing any sponsor data.
 */
export default function SponsorLoginPage() {
  const { user, login } = useAuth();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get("next") || "/sponsor";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("form"); // form | checking | denied
  const [deniedEmail, setDeniedEmail] = useState("");

  useEffect(() => {
    if (user) {
      setPhase("checking");
      adminApi
        .whoami()
        .then((r) => {
          const bindings = r?.sponsor_admin_bindings || [];
          if (bindings.length > 0) {
            window.location.replace(nextPath);
          } else {
            setDeniedEmail(user.email || "");
            setPhase("denied");
          }
        })
        .catch(() => setPhase("form"));
    }
  }, [user, nextPath]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email.trim(), password);
      const who = await adminApi.whoami();
      const bindings = who?.sponsor_admin_bindings || [];
      if (bindings.length > 0) {
        window.location.replace(nextPath);
        return;
      }
      setDeniedEmail(email.trim());
      setPhase("denied");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Sign-in failed — check your email and password."
      );
    } finally {
      setBusy(false);
    }
  };

  if (phase === "checking") {
    return (
      <div className="min-h-screen bg-[#0B1210] flex items-center justify-center text-[#B7D2AF]">
        <Loader2 size={18} className="animate-spin mr-2" /> Verifying sponsor access…
      </div>
    );
  }

  if (phase === "denied") {
    return (
      <div className="min-h-screen bg-[#0B1210] flex items-center justify-center px-6">
        <div
          data-testid="sponsor-login-denied"
          className="max-w-md w-full rounded-3xl border border-[#7C3E33]/50 bg-[#1A100E] p-8 text-center"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#E38680] mb-3">
            Access Denied
          </div>
          <h1 className="font-display text-3xl text-white mb-2">
            Not a sponsor account
          </h1>
          <p className="text-sm text-[#B7D2AF] leading-relaxed mb-6">
            Signed in as <span className="text-white">{deniedEmail}</span>. This
            account is not linked to any sponsor organization.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="sponsor-login-denied-retry"
              onClick={() => {
                try {
                  localStorage.removeItem("lg_token");
                } catch {}
                window.location.reload();
              }}
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-2.5 text-sm font-medium"
            >
              Sign in with a different account
            </button>
            <a href="/" className="text-sm text-[#B7D2AF] hover:text-white">
              Back to LifeGuide
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1210] text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#B7D2AF] hover:text-white text-sm mb-6"
        >
          ← Back to LifeGuide
        </Link>
        <div
          data-testid="sponsor-login-page"
          className="rounded-3xl border border-white/10 bg-[#141B18] p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-[#1F2A24] border border-[#4F6F52]/50 flex items-center justify-center">
              <Building2 size={16} className="text-[#8FBF87]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#B7D2AF]">
                Sponsor Portal
              </div>
              <div className="font-display text-xl">Sign in</div>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs text-[#8FA894] mb-1.5 uppercase tracking-[0.14em]">
                Email
              </label>
              <input
                data-testid="sponsor-login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#0B1210] border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/30"
                placeholder="you@your-org.org"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8FA894] mb-1.5 uppercase tracking-[0.14em]">
                Password
              </label>
              <input
                data-testid="sponsor-login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-[#0B1210] border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/30"
              />
            </div>
            <button
              data-testid="sponsor-login-submit"
              type="submit"
              disabled={busy || !email || !password}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Verifying…
                </>
              ) : (
                <>
                  <LogIn size={16} /> Sign in
                </>
              )}
            </button>
          </form>
          <p className="text-[11px] text-[#7C8F7C] mt-5 leading-relaxed">
            Sponsor accounts are created by invitation only. If you received
            an invitation email, use the link inside it to accept before
            signing in here.
          </p>
        </div>
      </div>
    </div>
  );
}
